import React, { useCallback, useState } from 'react';

import { AlertAnimation } from '@eika/alert-animation';
import { InfoMessage } from '@eika/attention-message';
import { Button } from '@eika/button';
import { WarningCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';

import { useConfig } from '../../../../defaultConfig';
import useValidateCrossBorderPayment from '../../../../fetchHooks/useValidateCrossBorderPayment';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import useHandleCrossBorderReload from '../../../../hooks/useHandleCrossBorderReload';
import useShowPaymentResult from '../../../../hooks/useShowPaymentResult';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import SigningContextFlow from '../../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../../signingContext/signingContext';
import { Actions } from '../../../../signingContext/signingTypes';
import { NavigationButtons } from '../../../shared/NavigationButtons';
import { currencyFormatter } from '../../../shared/numberFormat';
import { PaymentRetryContext } from '../../PaymentRetry';
import TransactionResultInfo from '../../TransactionResultInfo';
import createCrossBorderPaymentRequest from '../form/createCrossBorderPaymentRequest';
import useFormContext from '../form/useFormContext';
import FeeSkeleton from './FeeSkeleton';
import Summary from './Summary';
import ValidationWarnings from './ValidationWarnings';
import useReceiverUpdateOnSigningSuccess from './useReceiverUpdateOnSigningSuccess';

const SummaryPage: React.FC = () => {
    const translated = useTranslation();
    const { state, checkFormValidity } = useFormContext();
    const { targetFromAccount } = usePaymentAccountContext();

    const [blockedError, setBlockedError] = useState(false);
    const [invalidAccountNumberWarning, setInvalidAccountNumberWarning] = useState(false);
    const [sepaWarning, setSepaWarning] = useState(false);

    const {
        data: validateResponse,
        isLoading,
        error,
    } = useValidateCrossBorderPayment(
        state,
        (invalid) => setInvalidAccountNumberWarning(invalid),
        (error) => setBlockedError(error),
        (warning) => setSepaWarning(warning),
    );
    const { cancelCrossBorderPaymentLink } = useConfig();
    const {
        handleCancelSigningFlow,
        signingStatus,
        signingOperationPending,
        signingError,
        dispatch,
        startSigning,
        showPaymentRetry,
    } = useSigningContext(createCrossBorderPaymentRequest);
    useHandleCrossBorderReload();

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (await checkFormValidity()) {
                startSigning({ ...state, ignoreSepaWarning: sepaWarning });
            }
        },
        [checkFormValidity, startSigning, state, sepaWarning],
    );

    const createCrossBorderPaymentWithDuplicateOverride = useCallback(() => {
        startSigning({ ...state, acceptDuplicate: true, ignoreSepaWarning: sepaWarning });
    }, [sepaWarning, startSigning, state]);

    const { createReceiverIsLoading, updateReceiverIsLoading } = useReceiverUpdateOnSigningSuccess();

    const closeCrossBorderPayment = useCallback(() => {
        if (signingStatus !== 'UNKNOWN' && signingStatus !== 'REJECTED') {
            window.location.href = cancelCrossBorderPaymentLink!.url;
        }
    }, [cancelCrossBorderPaymentLink, signingStatus]);

    const showPaymentResult = useShowPaymentResult();

    if (showPaymentRetry) {
        return <PaymentRetryContext />;
    }

    if (createReceiverIsLoading || updateReceiverIsLoading) {
        return (
            <div className="summary-page__result-info">
                <Loader />
            </div>
        );
    }

    if (showPaymentResult) {
        return (
            <div className="summary-page__result-info">
                <TransactionResultInfo
                    type="PAYMENT"
                    retry={signingError ? handleCancelSigningFlow : closeCrossBorderPayment}
                    status={signingStatus}
                    error={signingError}
                    onClose={closeCrossBorderPayment}
                    fromAccountId={targetFromAccount?.accountId}
                    paymentDate={state.paymentDate}
                    onRetryManually={() => dispatch({ type: Actions.CLEAR_ERROR })}
                    onContinue={() => {
                        dispatch({ type: Actions.CLEAR_ERROR });
                        createCrossBorderPaymentWithDuplicateOverride();
                    }}
                    successButtons
                />
            </div>
        );
    }

    if (blockedError || invalidAccountNumberWarning) {
        return (
            <WarningCard title={translated.errors.somethingWentWrong} isModal icon={<AlertAnimation />}>
                <p>
                    {invalidAccountNumberWarning
                        ? translated.crossBorderPayment.summary.invalidAccountNumber
                        : translated.crossBorderPayment.summary.blockedErrorMessage}
                </p>
                <Button onClick={closeCrossBorderPayment}>{translated.ok}</Button>
            </WarningCard>
        );
    }

    return (
        <SigningContextFlow onClosePayment={closeCrossBorderPayment}>
            <h2>{translated.crossBorderPayment.summary.title}</h2>
            <form onSubmit={onSubmit}>
                <Summary />
                {isLoading && <FeeSkeleton />}
                {!isLoading && !error && !blockedError && validateResponse?.estimatedCosts && (
                    <InfoMessage
                        className="summary__margin-top"
                        icon={IconTypes.INFO_24}
                        text={`${translated.crossBorderPayment.summary.fee} 
                    ${currencyFormatter(
                        validateResponse.estimatedCosts.amount,
                        validateResponse.estimatedCosts.currency,
                    )} 
                    ${translated.crossBorderPayment.summary.feeMessage}`}
                    />
                )}
                {!isLoading && !error && !blockedError && !validateResponse?.estimatedCosts && (
                    <InfoMessage
                        className="summary__margin-top"
                        icon={IconTypes.INFO_24}
                        text={
                            state.chargeBearer &&
                            format(
                                translated.crossBorderPayment.summary.feeDescription,
                                translated.chargeBearerFeeDescription[state.chargeBearer],
                            )
                        }
                    />
                )}
                {validateResponse?.warnings && (
                    <>
                        {validateResponse?.warnings.map((warning) => (
                            <ValidationWarnings key={warning.code} warnings={warning} />
                        ))}
                    </>
                )}
                <NavigationButtons paymentButton paymentOperationPending={signingOperationPending} />
            </form>
        </SigningContextFlow>
    );
};

export default SummaryPage;
